import React, { useState } from 'react';
import { diagnosticsForDisplay } from '../../features/simulation/preflightDiagnostics';
import { useAppSelector } from '../../hooks/redux';

/** Blocking findings only — validate warnings stay off the panel. */
const TONE = {
  error: {
    shell: 'border-red-200',
    header: 'border-b border-red-100',
    title: 'text-red-800',
    count: 'text-red-500',
    button: 'border-red-200 text-red-700 hover:bg-red-50',
    badge: 'bg-red-100 text-red-700',
    card: 'border-red-100 bg-red-50/60',
    code: 'text-red-800',
    divider: 'border-red-100',
  },
  warning: {
    shell: 'border-amber-200',
    header: 'border-b border-amber-100',
    title: 'text-amber-800',
    count: 'text-amber-500',
    button: 'border-amber-200 text-amber-700 hover:bg-amber-50',
    badge: 'bg-amber-100 text-amber-700',
    card: 'border-amber-100 bg-amber-50/60',
    code: 'text-amber-800',
    divider: 'border-amber-100',
  },
} as const;

const SimulationErrorsPanel: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const findings = diagnosticsForDisplay(
    useAppSelector((state) => state.simulation.live.diagnostics)
  );

  if (findings.length === 0) return null;

  const errorCount = findings.filter((finding) => finding.severity === 'error').length;
  const tone = errorCount > 0 ? TONE.error : TONE.warning;
  const label = errorCount > 0 ? 'Errors' : 'Warnings';

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-expanded="false"
        className={`absolute right-96 top-3 z-30 flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-xs font-semibold shadow-sm transition-colors ${tone.button}`}
      >
        <span>{label}</span>
        <span className={`min-w-5 rounded-full px-1.5 py-0.5 text-center text-[10px] ${tone.badge}`}>
          {findings.length}
        </span>
      </button>
    );
  }

  return (
    <section
      className={`absolute right-96 top-3 z-30 flex max-h-[calc(100%-1.5rem)] w-80 flex-col overflow-hidden rounded-xl border bg-white shadow-lg ${tone.shell}`}
    >
      <button
        type="button"
        onClick={() => setExpanded(false)}
        aria-expanded="true"
        className={`flex w-full items-center justify-between px-3 py-2.5 text-left ${tone.header}`}
      >
        <span className={`text-xs font-semibold ${tone.title}`}>
          {label} <span className={`ml-1 ${tone.count}`}>({findings.length})</span>
        </span>
        <span className="text-xs text-slate-400" aria-hidden>
          ‹
        </span>
      </button>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5 scrollbar-thin">
        {findings.map((finding) => {
          const row = finding.severity === 'error' ? TONE.error : TONE.warning;
          return (
            <article
              key={finding.diagnosticId}
              className={`rounded-lg border p-2.5 ${row.card}`}
            >
              <div className="mb-1 flex items-start justify-between gap-2">
                <span className={`break-all text-[10px] font-semibold ${row.code}`}>
                  {finding.code}
                </span>
                <div className="flex shrink-0 items-center gap-1">
                  {/* Where it was found: from the request, or by the running fab. */}
                  {finding.phase === 'preflight' && (
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-500">
                      before start
                    </span>
                  )}
                  {(finding.occurrences ?? 1) > 1 && (
                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${row.badge}`}>
                      ×{finding.occurrences}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-700">{finding.message}</p>
              {finding.suggestion && (
                <div className={`mt-2 border-t pt-2 ${row.divider}`}>
                  <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                    Suggestion
                  </p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600">
                    {finding.suggestion}
                  </p>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default SimulationErrorsPanel;
